/**
 * Tenant telemetry export (REQ-LOG-001/002, ADR-136/137).
 *
 * The export destination is platform-managed (bucket per tenant, `logs/`).
 * There are no customer credentials and no consent step (D2/D3/D5): the page
 * only enables the export and configures filters.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';

export interface N2Destination {
  provider: 'aws_s3' | 'azure_blob' | 'gcs';
  bucket?: string;
  region?: string;
  prefix?: string;
  endpoint_url?: string | null;
  account_url?: string;
  container?: string;
  auth_mode: string;
  role_arn?: string;
  client_id?: string;
  tenant_id?: string;
}

export interface TelemetryExportConfig {
  enabled: boolean;
  schema_version?: number;
  migration_required?: boolean;
  destination?: N2Destination;
  credential_secret_id?: string;
  prefix: string;
  event_types: string[];
  min_level: string;
  batch_size: number;
  batch_interval_seconds: number;
}

export interface TenantSecret {
  id: string;
  name: string;
  secret_type: string | null;
  scope: string;
}

export interface TelemetryConfig {
  tenant_id: string;
  enabled: boolean;
  export: TelemetryExportConfig;
}

export interface TelemetryIntegration {
  id: string;
  kind: string;
  enabled: boolean;
  prefix: string;
  config: Record<string, unknown>;
}

export interface TelemetryHealth {
  tenant_id: string;
  export_enabled: boolean;
  consecutive_failures: number;
  first_failure_at: string | null;
  last_success_at: string | null;
  last_failure_at: string | null;
  last_exported_at: string | null;
}

export interface TelemetryDelivery {
  delivery_id: string;
  mode: string;
  status: string;
  batch_size: number;
  attempts: number;
  error: string | null;
  created_at: string;
}

export const telemetryKeys = {
  config: ['telemetry', 'config'] as const,
  secrets: ['telemetry', 'secrets'] as const,
  integrations: ['telemetry', 'integrations'] as const,
  health: ['telemetry', 'health'] as const,
  deliveries: ['telemetry', 'deliveries'] as const,
};

export function useTelemetryConfig() {
  const { currentTenant } = useTenant();
  return useQuery({
    queryKey: telemetryKeys.config,
    enabled: Boolean(currentTenant?.id),
    queryFn: async () => {
      const { data } = await apiClient.get<TelemetryConfig>('/telemetry/config');
      return data;
    },
  });
}

export function useTenantSecrets() {
  const { currentTenant } = useTenant();
  return useQuery({
    queryKey: [...telemetryKeys.secrets, currentTenant?.id],
    enabled: Boolean(currentTenant?.id),
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: TenantSecret[] }>('/secrets', {
        params: { scope: 'tenant', page_size: 100 },
      });
      return data.data;
    },
  });
}

export function useTestTelemetryDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{ ok: boolean }>('/telemetry/config/test');
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: telemetryKeys.config });
      void queryClient.invalidateQueries({ queryKey: telemetryKeys.health });
    },
  });
}

export function useTelemetryIntegrations() {
  const { currentTenant } = useTenant();
  return useQuery({
    queryKey: telemetryKeys.integrations,
    enabled: Boolean(currentTenant?.id),
    queryFn: async () => {
      const { data } = await apiClient.get<TelemetryIntegration[]>(
        '/telemetry/integrations',
      );
      return data;
    },
  });
}

export function useTelemetryHealth() {
  const { currentTenant } = useTenant();
  return useQuery({
    queryKey: telemetryKeys.health,
    enabled: Boolean(currentTenant?.id),
    queryFn: async () => {
      const { data } = await apiClient.get<TelemetryHealth>('/telemetry/health');
      return data;
    },
  });
}

export function useTelemetryDeliveries(limit = 20) {
  const { currentTenant } = useTenant();
  return useQuery({
    queryKey: [...telemetryKeys.deliveries, limit],
    enabled: Boolean(currentTenant?.id),
    queryFn: async () => {
      const { data } = await apiClient.get<TelemetryDelivery[]>(
        `/telemetry/deliveries?limit=${limit}`,
      );
      return data;
    },
  });
}

export function useUpdateTelemetryConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<TelemetryExportConfig>) => {
      const { data } = await apiClient.patch<TelemetryConfig>('/telemetry/config', {
        export: payload,
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: telemetryKeys.config });
      void queryClient.invalidateQueries({ queryKey: telemetryKeys.health });
    },
  });
}

export function useTelemetryActions() {
  const queryClient = useQueryClient();
  const integrationPath = (id: string) => `/telemetry/integrations/${id}`;
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: telemetryKeys.health });
    void queryClient.invalidateQueries({ queryKey: telemetryKeys.deliveries });
    void queryClient.invalidateQueries({ queryKey: telemetryKeys.integrations });
  };
  return {
    test: useMutation({
      mutationFn: async (id: string) => {
        const { data } = await apiClient.post(`${integrationPath(id)}/test`);
        return data;
      },
      onSuccess: invalidate,
    }),
    pause: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.post(`${integrationPath(id)}/pause`);
      },
      onSuccess: invalidate,
    }),
    resume: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.post(`${integrationPath(id)}/resume`);
      },
      onSuccess: invalidate,
    }),
    backfill: useMutation({
      mutationFn: async (id: string) => {
        const { data } = await apiClient.post(
          `${integrationPath(id)}/backfill?since_hours=24&limit=200`,
        );
        return data;
      },
      onSuccess: invalidate,
    }),
  };
}
